import React, { useEffect, useState } from 'react';
import axios from 'axios';

const AdministratorUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [roleId, setRoleId] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [fiscalName, setFiscalName] = useState('');
  const [selectedModes, setSelectedModes] = useState([]); // filtros de modo
  const [selectedStatus, setSelectedStatus] = useState([]); // filtros Activo/Inactivo
  const [monthlyModes, setMonthlyModes] = useState([]);

  const fetchUsers = (
    page = 1,
    overrideRoleId = null,
    overrideClientFilter = null,
    overrideModes = null,
    overrideStatus = null
  ) => {
    setLoading(true);
    setError(null);

    axios
      .get('/api/administrator/users', {
        params: {
          page,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          role_id: (overrideRoleId !== null ? overrideRoleId : roleId) || undefined,
          client_filter:
            (overrideClientFilter !== null ? overrideClientFilter : clientFilter) || undefined,
          fiscal_name: fiscalName || undefined,
          modes: (overrideModes !== null ? overrideModes : selectedModes).length
            ? (overrideModes !== null ? overrideModes : selectedModes).join(',')
            : undefined,
          status: (overrideStatus !== null ? overrideStatus : selectedStatus).length
            ? (overrideStatus !== null ? overrideStatus : selectedStatus).join(',')
            : undefined,
        },
      })
      .then((response) => {
        const data = response.data;
        setUsers(data.data || []);
        setCurrentPage(data.current_page || 1);
        setLastPage(data.last_page || 1);
        setTotalUsers(data.total || 0);
        setMonthlyModes(data.monthly_modes || []);
      })
      .catch(() => {
        setError('Ocurrió un error al cargar los usuarios.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Cargar por defecto el último mes (el backend ya aplica ese rango si no se envían fechas)
  useEffect(() => {
    fetchUsers(1);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage || page === currentPage) return;
    fetchUsers(page);
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (roleId) params.append('role_id', roleId);
    if (clientFilter) params.append('client_filter', clientFilter);
    if (fiscalName) params.append('fiscal_name', fiscalName);
    if (selectedModes.length) params.append('modes', selectedModes.join(','));
    if (selectedStatus.length) params.append('status', selectedStatus.join(','));

    const url = `/api/administrator/users/export${params.toString() ? `?${params.toString()}` : ''}`;
    window.location.href = url;
  };

  const toggleMode = (mode) => {
    setSelectedModes((prev) => {
      let next;
      if (prev.includes(mode)) {
        next = prev.filter((m) => m !== mode);
      } else {
        next = [...prev, mode];
      }

      // Siempre que cambie el filtro de modos, recargamos desde el backend
      // para actualizar paginación y totales, respetando los demás filtros.
      fetchUsers(1, null, null, next);

      return next;
    });
  };

  const toggleStatus = (statusLabel) => {
    setSelectedStatus((prev) => {
      let next;
      if (prev.includes(statusLabel)) {
        next = prev.filter((s) => s !== statusLabel);
      } else {
        next = [...prev, statusLabel];
      }

      fetchUsers(1, null, null, null, next);
      return next;
    });
  };

  const filteredUsers = users.filter((user) => {
    const modeOk = !user.mode || selectedModes.length === 0 || selectedModes.includes(user.mode);
    const statusLabel = user.company_status || (user.status === 1 ? 'Activo' : 'Inactivo');
    const statusOk = selectedStatus.length === 0 || selectedStatus.includes(statusLabel);
    return modeOk && statusOk;
  });

  return (
    <div className="mt-4">
      <h3>Reporte de usuarios</h3>

      {!loading && !error && (
        <>
          <div className="mb-3 mt-2">
            <h5 className="mb-2" style={{ fontWeight: 600 }}>Descripción de modos</h5>
            <ul className="list-unstyled mb-0">
              <li className="d-flex align-items-center mb-1">
                <span
                  className="badge mr-2"
                  style={{
                    backgroundColor: '#eef2ff',
                    color: '#4f46e5',
                    borderRadius: '999px',
                    minWidth: '18px',
                    height: '18px',
                  }}
                />
                <span>
                  <strong style={{ color: '#4f46e5' }}>Completador:</strong>{' '}
                  Usuario creado por la invitación de un cliente.
                </span>
              </li>
              <li className="d-flex align-items-center mb-1">
                <span
                  className="badge mr-2"
                  style={{
                    backgroundColor: '#ecfeff',
                    color: '#0891b2',
                    borderRadius: '999px',
                    minWidth: '18px',
                    height: '18px',
                  }}
                />
                <span>
                  <strong style={{ color: '#0891b2' }}>Nuevo Registro:</strong>{' '}
                  Usuario que completó su registro pero aún no ha hecho uso de la plataforma.
                </span>
              </li>
              <li className="d-flex align-items-center">
                <span
                  className="badge mr-2"
                  style={{
                    backgroundColor: '#ecfdf5',
                    color: '#15803d',
                    borderRadius: '999px',
                    minWidth: '18px',
                    height: '18px',
                  }}
                />
                <span>
                  <strong style={{ color: '#15803d' }}>Cliente:</strong>{' '}
                  Usuario que se ha registrado y ha creado expedientes (incluye usuarios colaboradores).
                </span>
              </li>
            </ul>
          </div>

          <div className="mb-2">
            <strong>Total de usuarios:</strong> {totalUsers}
          </div>

          {monthlyModes.length > 0 && (
            <div className="mb-4 d-flex flex-wrap">
              {monthlyModes.map((item, index) => {
                const { year, month, completador, nuevo_usuario, cliente } = item;

                const monthNames = [
              'Enero',
              'Febrero',
              'Marzo',
              'Abril',
              'Mayo',
              'Junio',
              'Julio',
              'Agosto',
              'Septiembre',
              'Octubre',
              'Noviembre',
              'Diciembre',
            ];

                const monthLabel = monthNames[(month || 1) - 1] || month;

                return (
                  <div
                    key={index}
                    className="card mr-3 mb-3"
                    style={{ minWidth: '220px', maxWidth: '260px', borderRadius: '12px' }}
                  >
                    <div className="card-body p-3">
                      <h5 className="card-title mb-3" style={{ fontWeight: 600 }}>
                        {monthLabel} {year}
                      </h5>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Completador</span>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: '#eef2ff',
                            color: '#4f46e5',
                            borderRadius: '999px',
                            minWidth: '32px',
                            textAlign: 'center',
                          }}
                        >
                          {completador}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span>Nuevo Registro</span>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: '#ecfeff',
                            color: '#0891b2',
                            borderRadius: '999px',
                            minWidth: '32px',
                            textAlign: 'center',
                          }}
                        >
                          {nuevo_usuario}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <span>Clientes</span>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: '#ecfdf5',
                            color: '#15803d',
                            borderRadius: '999px',
                            minWidth: '32px',
                            textAlign: 'center',
                          }}
                        >
                          {cliente}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </>
      )}

      <form className="mb-3" onSubmit={handleSearch}>
        {/* Primera línea: fechas, rol, cliente, botones */}
        <div className="d-flex flex-wrap align-items-end mb-2">
          <div className="form-group mr-3 mb-2">
            <label className="mr-1 mb-1 d-block">Desde</label>
            <input
              type="date"
              className="form-control"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="form-group mr-3 mb-2">
            <label className="mr-1 mb-1 d-block">Hasta</label>
            <input
              type="date"
              className="form-control"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="form-group mr-3 mb-2">
            <label className="mr-1 mb-1 d-block">Rol</label>
            <select
              className="form-control"
              value={roleId}
              onChange={(e) => {
                const newRoleId = e.target.value;
                setRoleId(newRoleId);
                fetchUsers(1, newRoleId);
              }}
            >
              <option value="">Todos</option>
              <option value="1">Owner</option>
              <option value="2">Colaborador</option>
              <option value="3">Multicompletador</option>
            </select>
          </div>

          <div className="form-group mr-3 mb-2">
            <label className="mr-1 mb-1 d-block">Cliente</label>
            <select
              className="form-control"
              value={clientFilter}
              onChange={(e) => {
                const newClientFilter = e.target.value;
                setClientFilter(newClientFilter);
                fetchUsers(1, null, newClientFilter);
              }}
            >
              <option value="">Todos</option>
              <option value="client">Cliente</option>
              <option value="non_client">No cliente</option>
            </select>
          </div>

          <div className="form-group mr-3 mb-2" style={{ minWidth: '260px' }}>
            <label className="mr-1 mb-1 d-block">Nombre fiscal</label>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por person.fiscal_name"
              value={fiscalName}
              onChange={(e) => setFiscalName(e.target.value)}
            />
          </div>

          <div className="ml-auto mb-2 d-flex">
            <button type="submit" className="btn btn-primary mr-2">
              Buscar
            </button>

            <button type="button" className="btn btn-success" onClick={handleExport}>
              Exportar a Excel
            </button>
          </div>
        </div>

        {/* Segunda línea: modos con checkboxes usando el estilo de la librería (como formatos) */}
        <div className="d-flex align-items-center flex-wrap mt-1">
          <span className="mr-3 font-weight-bold">Modo</span>
          <div className="checkbox-inline form-group mb-0 mr-6">
            <label className="checkbox checkbox-lg checkbox-square mr-3">
              <input
                type="checkbox"
                id="modeCompletador"
                checked={selectedModes.includes('Completador')}
                onChange={() => toggleMode('Completador')}
              />
              <span></span>
              Completador
            </label>

            <label className="checkbox checkbox-lg checkbox-square mr-3">
              <input
                type="checkbox"
                id="modeNuevoRegistro"
                checked={selectedModes.includes('Nuevo Registro')}
                onChange={() => toggleMode('Nuevo Registro')}
              />
              <span></span>
              Nuevo Registro
            </label>

            <label className="checkbox checkbox-lg checkbox-square mr-3">
              <input
                type="checkbox"
                id="modeCliente"
                checked={selectedModes.includes('Cliente')}
                onChange={() => toggleMode('Cliente')}
              />
              <span></span>
              Cliente
            </label>
          </div>

          <span className="mr-3 font-weight-bold">Estatus</span>
          <div className="checkbox-inline form-group mb-0">
            <label className="checkbox checkbox-lg checkbox-square mr-3">
              <input
                type="checkbox"
                id="statusActivo"
                checked={selectedStatus.includes('Activo')}
                onChange={() => toggleStatus('Activo')}
              />
              <span></span>
              Activo
            </label>

            <label className="checkbox checkbox-lg checkbox-square mr-3">
              <input
                type="checkbox"
                id="statusInactivo"
                checked={selectedStatus.includes('Inactivo')}
                onChange={() => toggleStatus('Inactivo')}
              />
              <span></span>
              Inactivo
            </label>
          </div>
        </div>
      </form>

      {loading && <div>Cargando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-bordered w-100">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Empresa</th>
                  <th>Tipo de empresa</th>
                  <th>Rol</th>
                  <th>Cliente</th>
                  <th>Modo</th>
                  <th>Total expedientes solicitados</th>
                  <th>Estatus empresa</th>
                  <th>Estatus usuario</th>
                  <th>Fecha creación</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="12" className="text-center">
                      No se encontraron resultados.
                    </td>
                  </tr>
                )}
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.person ? user.person.fiscal_name : ''}</td>
                    <td>{user.person ? user.person.person_type : ''}</td>
                    <td>
                      {user.role_id === 1
                        ? 'Owner'
                        : user.role_id === 2
                        ? 'Colaborador'
                        : user.role_id === 3
                        ? 'Multicompletador'
                        : ''}
                    </td>
                    <td>
                      {user.is_client ? (
                        <span className="badge badge-success">Cliente</span>
                      ) : (
                        <span className="badge badge-secondary">No cliente</span>
                      )}
                    </td>
                    <td>{user.mode}</td>
                    <td>{user.requested_dossiers_total || 0}</td>
                    <td>{user.company_status || (user.person && !user.person.deleted_at ? 'Activo' : 'Inactivo')}</td>
                    <td>{user.status === 1 ? 'Activo' : 'Inactivo'}</td>
                    <td>{user.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {lastPage > 1 && (
            <nav>
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Anterior
                  </button>
                </li>
                <li className="page-item disabled">
                  <span className="page-link">
                    Página {currentPage} de {lastPage}
                  </span>
                </li>
                <li className={`page-item ${currentPage === lastPage ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Siguiente
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default AdministratorUsers;
